/**
 * @fileoverview Contact Us Page
 *
 * Purpose: Public contact page directing enquiries to email.
 * URL: /contact
 * Protection: Public (no auth required)
 */

import { createFileRoute } from "@tanstack/react-router";
import { useAuth } from "@workos-inc/authkit-react";
import { Route as AuthLoginRoute } from "@/routes/_app/login/_layout.index";
import { Route as DashboardRoute } from "@/routes/_app/_auth/dashboard/_layout.index";
import { ScrollingNav } from "./_home/-ui.scrolling-nav";
import { FooterSection } from "./_home/-sections.footer";
import { Mail, MessageCircle, Clock } from "lucide-react";
import { CookieBanner } from "./_home/-ui.cookie-banner";

export const Route = createFileRoute("/contact")({
  component: ContactPage,
});

const CONTACT_EMAIL = "josh@morgan.so";

function ContactPage() {
  const { user, isLoading } = useAuth();

  return (
    <div className="relative w-full">
      <ScrollingNav
        isAuthenticated={!!user}
        isLoading={isLoading}
        loginPath={AuthLoginRoute.fullPath}
        dashboardPath={DashboardRoute.fullPath}
      />

      {/* Hero - pulled up behind the sticky nav so the white logo sits on the dark background */}
      <section className="relative -mt-[65px] overflow-hidden bg-[#1a4025] pb-16 pt-[calc(6rem_+_65px)]">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h1 className="text-4xl font-medium tracking-tight text-white md:text-5xl">
            We'd love to hear from you
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-white/75">
            Whether you want to learn more about Morgan or just want to say
            hello, please reach out and we'll get back to you.
          </p>
        </div>
      </section>

      {/* Contact card */}
      <section className="py-20">
        <div className="mx-auto max-w-2xl px-6">
          <div className="rounded-2xl border border-border/60 bg-card p-8 text-center sm:p-12">
            <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <Mail className="h-7 w-7 text-primary" />
            </div>
            <h2 className="text-2xl font-semibold text-foreground">
              Send us an email
            </h2>
            <p className="mx-auto mt-3 max-w-md text-base leading-relaxed text-muted-foreground">
              The fastest way to reach us. Email goes straight to the team — no
              ticket queues, no chatbots.
            </p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              <Mail className="h-4 w-4" />
              {CONTACT_EMAIL}
            </a>
          </div>

          {/* Supporting points */}
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-border/50 bg-card p-6">
              <MessageCircle className="mb-3 h-6 w-6 text-primary" />
              <h3 className="text-base font-semibold text-foreground">
                Real people
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Your message lands directly with the people building Morgan.
              </p>
            </div>
            <div className="rounded-xl border border-border/50 bg-card p-6">
              <Clock className="mb-3 h-6 w-6 text-primary" />
              <h3 className="text-base font-semibold text-foreground">
                Quick replies
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                We aim to respond to every enquiry within one business day.
              </p>
            </div>
          </div>
        </div>
      </section>

      <FooterSection />

      <CookieBanner />
    </div>
  );
}
